








































































































































































































import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator'
import { Referral } from '@/api/model/referral'
import { OrderAPI, PipelinePhaseAPI, StatusAPI, StatusReasonAPI } from '@/api/backend-client'
import { availableReferralStatuses, ReferralStatus } from '@/api/types'
import { StatusReason } from '@/api/model/status-reason'
import { Order } from '@/api/model/order'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { cloneDeep } from 'lodash'
import { InputSubSelectOption } from '@/components/input/InputSubSelect.vue'
import { Status } from '@/api/model/status'

@Component
export default class ReferralStatusForm extends Vue {
    @Prop() readonly referral!: Referral
    @Inject() readonly saveReferral!: (referral: Referral) => Promise<void>
    @Inject() readonly reloadReferral!: () => Promise<void>
    orders: Order[] = []
    phases: PipelinePhase[] = []
    statusses: Status[] = []
    statusReasons: StatusReason[] = []
    loading = false
    phaseLoading = false
    chanceLoading = false
    phaseLocalValue = []
    phaseValue = []
    phaseSelectLabel = []
    phaseReasonLabel = []
    statusLocalValue = []
    statusValue = []
    statusSelectLabel = []
    statusReason: StatusReason = null
    statusReasonLabel = []
    createOrderModalVisible = false
    createActionModalVisible = false
    confirmReferralStatusModalVisible = false
    duplicateReferralModalVisible = false
    deleteReferralModalVisible = false
    reopenReferralModalVisible = false
    transferReferralModalVisible = false
    followupChanceModalVisible = false
    closeReferralModalVisible = false
    reopenTaskModalVisible = false

    async mounted() {
        this.loading = true
        await this.reloadLocalValue()
        this.loading = false
    }

    @Watch('referral')
    async reloadLocalValue(): Promise<void> {
        this.phases = (await PipelinePhaseAPI.getAll({ pipeline: this.referral.pipeline.resourceIdentifier }))
            .sort((a, b) => a.compareTo(b))
            .filter(phase => phase.active)

        this.statusses = await StatusAPI.getAll({ company: this.$currentCompany.resourceIdentifier })

        this.statusReasons = (await StatusReasonAPI.getAll({ company: this.$currentCompany.resourceIdentifier }))
            .sort((a, b) => a.name.localeCompare(b.name))

        this.orders = await OrderAPI.getAll({ referral: this.referral.id })

        if (this.referral.phase) {
            this.setPhaseProperties([this.referral.phase.resourceIdentifier, this.referral?.newStatusReason?.resourceIdentifier])
        }

        this.setStatusProperties([this.referral.status, this.referral?.newStatusReason?.resourceIdentifier])
    }

    async savePhase(phaseValue: any[]) {
        this.phaseLoading = true

        await this.setPhaseProperties(phaseValue)

        const referral = cloneDeep<any>(this.referral)
        referral.phase = phaseValue[0]
        referral.statusReason = null
        referral.newStatusReason = phaseValue[1]

        await this.saveReferral(referral)
        this.phaseLoading = false
    }

    async saveChance(chance: number) {
        this.chanceLoading = true
        const referral = cloneDeep<any>(this.referral)
        referral.score = chance

        await this.saveReferral(referral)
        this.chanceLoading = false
    }

    async saveStatus(statusValue: any[]) {
        await this.setStatusProperties(statusValue)

        const referral = cloneDeep<any>(this.referral)
        referral.status = statusValue[0]
        referral.statusReason = null
        referral.newStatusReason = statusValue[1]

        await this.saveReferral(referral)

        // When status changed to successful.
        if (referral.status === ReferralStatus.SUCCESSFUL) {

            // And orders are enabled and a mapping exists, an order will be created (backend)
            if (this.ordersEnabled && this.hasOrderMapping) {
                this.$notify({
                    group: 'information',
                    type: 'success',
                    title: this.$t('forms.edit-task-form.automated-success.title').toString(),
                    text: this.$t('forms.edit-task-form.automated-success.message').toString(),
                })

                // need to reload in order to show the comment we promised above
                this.$emit('reload')
            }
        }

        this.confirmReferralStatusModalVisible = false
    }

    setPhaseProperties(phaseValue: any[]) {
        if (phaseValue.length <= 0) {
            return
        }

        this.phaseValue = phaseValue
        this.phaseLocalValue = phaseValue
        this.phaseSelectLabel = [phaseValue[0]]
        this.phaseReasonLabel = [phaseValue[1]]

        this.statusReason = this.statusReasons.filter(statusReason => statusReason.resourceIdentifier === phaseValue[1])[0]
    }

    setStatusProperties(statusValue: any[]) {
        if (statusValue.length <= 0) {
            return
        }

        this.statusValue = statusValue
        this.statusLocalValue = statusValue
        this.statusSelectLabel = [statusValue[0]]
        this.statusReasonLabel = [statusValue[1]]

        this.statusReason = this.statusReasons.filter(statusReason => statusReason.resourceIdentifier === statusValue[1])[0]
    }

    openModal(modalName: string) {
        this.hideModals()
        this[modalName + 'Visible'] = true
    }

    hideModals() {
        this.duplicateReferralModalVisible = false
        this.transferReferralModalVisible = false
        this.createOrderModalVisible = false
        this.createActionModalVisible = false
        this.reopenReferralModalVisible = false
        this.deleteReferralModalVisible = false
        this.reopenTaskModalVisible = false
        this.confirmReferralStatusModalVisible = false
    }

    getStyleByPhaseName(value: string) {
        const currentPhase = this.phases.find(phase => phase.name === value)
        const color = currentPhase ? currentPhase.color : 'rgba(0,0,0,0)'

        return {
            borderColor: color,
        }
    }

    getStyleByStatusName(value: string) {
        const statusColors = {
            [this.$t('referral.status.' + ReferralStatus.OPEN).toString()]: `rgb(110,110,110)`,
            [this.$t('referral.status.' + ReferralStatus.SUCCESSFUL).toString()]: `rgba(0, 163, 137, 1)`,
            [this.$t('referral.status.' + ReferralStatus.CLOSED).toString()]: `rgba(222, 64, 119, 1)`,
        }

        const color = Object.values(statusColors)[Object.keys(statusColors).indexOf(value)]

        return color ? { color } : null
    }

    get canEdit(): boolean {
        if (!this.$currentCompany || !this.referral || !this.referral.assignedToBranch) {
            return false
        }

        return this.$currentCompany.id === this.referral.assignedToBranch.company.id
    }

    get canEditStatus() {
        return this.isOpen || this.isChance
    }

    get isOpen(): boolean {
        return this.referral.status === ReferralStatus.OPEN
    }

    get isChance(): boolean {
        return this.referral.status === ReferralStatus.PROCESSED
    }

    get isClosed(): boolean {
        return this.referral.status === ReferralStatus.CLOSED
    }

    get isSucces(): boolean {
        return this.referral.status === ReferralStatus.SUCCESSFUL
    }

    get canReopen(): boolean {
        if (!this.canEdit || !this.$currentCompany.permissions) {
            return false
        }

        const permission = this.$currentCompany.permissions.find(permissionLookup => permissionLookup.name === 'referral.reopen')

        return (this.isClosed || this.isSucces) && permission.roles.includes(this.$currentUser.role)
    }

    get isAssigned(): boolean {
        return this.referral.assignedTo !== null
    }

    get hasOrders(): boolean {
        return this.orders.length > 0
    }

    get canDeleteReferral(): boolean {
        return this.$currentUser.isCompanyManager
    }

    get canCreateOrder(): boolean {
        if (!this.$currentCompany.orderSettings) {
            return false
        }

        return this.$currentCompany.orderSettings.enabled &&
            this.$currentCompany.orderSettings.allowManualCreation &&
            this.referral.status === ReferralStatus.SUCCESSFUL
    }

    get linkExternalProjectModalVisible() {

        // Source company must be different from target company.
        return this.referral.referredByBranch.company.id !== this.referral.assignedToBranch.company.id

            // Assigned to branch must belong to our current company.
            && this.referral.assignedToBranch.company.id === this.$currentCompany.id

            // Source company must have set a project.
            && this.referral.project

            // Current company must not yet have selected a project.
            && this.referral.assigneeProject === null
    }

    buildOptionsForStatus(status: string, statusReasons: StatusReason[], phase: PipelinePhase): InputSubSelectOption[] {
        const statusObject = this.statusses
            .find(entry => entry.status === status)

        const options = statusReasons
            .filter(statusReason => statusReason.pipeline && statusReason.pipeline.id === this.referral.pipeline.id)
            .filter(statusReason => {
                return !phase ? true : statusReason.phases
                    .map(statusReasonPhase => statusReasonPhase.id)
                    .includes(phase.id)
            }).map(statusReason => {
                return ({
                    label: statusReason.name,
                    value: statusReason.resourceIdentifier,
                    options: [],
                })
            })

        let noStatusReasonEnabled = false

        if (phase) {
            noStatusReasonEnabled = phase.noStatusReasonEnabled
        } else if (statusObject) {
            noStatusReasonEnabled = statusObject.noReasonEnabled
        }

        if (options.length > 0 && noStatusReasonEnabled) {
            options.unshift({
                label: 'Niet van toepassing',
                value: null,
                options: [],
            })
        }

        return options
    }

    get activeStatusReasons(): StatusReason[] {
        return this.statusReasons.filter(statusReason => statusReason.active)
    }

    get statusOptions(): InputSubSelectOption[] {
        return availableReferralStatuses(this.$currentCompany).map((status: ReferralStatus) => {
            const statusReasons = this.activeStatusReasons
                .filter(statusReason => statusReason.status === status && statusReason.status !== ReferralStatus.OPEN)

            const statusObject = this.statusses
                .find(entry => entry.status === status)

            let optionsHeader = this.$t('forms.referral_status_form.pickReason').toString()
            if (statusObject.statusReasonRequired) {
                optionsHeader += ' ' + this.$t('forms.referral_status_form.pickReasonRequired')
            } else {
                optionsHeader += ' ' + this.$t('forms.referral_status_form.pickReasonOptional')
            }

            return {
                label: this.$t('referral.status.' + status).toString(),
                value: status,
                optionsHeader,
                options: this.buildOptionsForStatus(status, statusReasons, null),
                clickable: !statusObject.statusReasonRequired,
            }
        })
    }

    get phaseOptions() {
        const statusReasons = this.activeStatusReasons
            .filter(statusReason => statusReason.status === ReferralStatus.OPEN)

        return this.phases.map(phase => {
            let optionsHeader = this.$t('forms.referral_status_form.pickReason').toString()
            if (phase.statusReasonRequired) {
                optionsHeader += ' ' + this.$t('forms.referral_status_form.pickReasonRequired')
            } else {
                optionsHeader += ' ' + this.$t('forms.referral_status_form.pickReasonOptional')
            }

            return {
                label: phase.name,
                value: phase.resourceIdentifier,
                optionsHeader,
                options: this.buildOptionsForStatus(ReferralStatus.OPEN, statusReasons, phase),
                clickable: !phase.statusReasonRequired,
            }
        })
    }

    get phaseId() {
        return this.referral.phase.resourceIdentifier
    }

    get chanceValue() {
        return this.referral.score
    }

    get chanceOptions() {
        return [{
            label: this.$t('forms.referral_status_form.success-chance.1'),
            value: 1,
            options: [],
        }, {
            label: this.$t('forms.referral_status_form.success-chance.0'),
            value: 0,
            options: [],
        }, {
            label: this.$t('forms.referral_status_form.success-chance.-1'),
            value: -1,
            options: [],
        }]
    }

    get actionOptions() {
        const actions = []

        actions.push({
            label: this.$t('forms.duplicate-referral-form.button').toString(),
            value: 'duplicateReferralModal',
            options: [],
        })

        if (this.canCreateOrder) {
            actions.push({
                label: this.$t('forms.create-order.button').toString(),
                value: 'createOrderModal',
                options: [],
            })
        }

        if (this.canEdit) {
            actions.push({
                label: this.$t('referral.transfer.button').toString(),
                value: 'transferReferralModal',
                options: [],
            })
        }

        if (this.canEdit && this.isAssigned) {
            actions.push({
                label: this.$t('forms.edit-task-form.button.plan-appointment').toString(),
                value: 'createActionModal',
                options: [],
            })
        }

        if (this.canReopen) {
            actions.push({
                label: this.$t('forms.reopen-task-form.button.open' + (this.referral.status === 'processed' ? '-processed' : '')).toString(),
                value: 'reopenReferralModal',
                options: [],
            })
        }

        if (this.canDeleteReferral) {
            actions.push({
                label: this.$t('referral.delete').toString(),
                value: 'deleteReferralModal',
                options: [],
            })
        }

        return actions
    }

    get ordersEnabled(): boolean {
        return this.$currentCompany.orderSettings?.enabled || false
    }

    get hasOrderMapping(): boolean {
        return this.$currentCompany.orderSettings.getMapping(this.referral.pipeline.id) !== null
    }
}
